import cn from 'classnames';
import { format, subMonths } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { Space } from '@sravni/react-design-system';
import { useBoolean, useIsMobile, useIsTablet, usePrevious } from '@sravni/react-utils';
import { getOrganizationHelpers } from '@sravni/ugc/lib/organizations/helpers';

import { yupResolver } from '@hookform/resolvers/yup';

import { LIST } from '../../constants/routes';
import { sendAuthEvent, sendReviewEvent } from '../../helpers/analyticsEvents';
import { checkIfModerationComplete } from '../../helpers/reviews';
import { ICompanyResponse, IRepresentative } from '../../interfaces/reviews';
import { createReview, loadOrganizationResponse, loadRepresentative, updateReview } from '../../services/reviews';
import {
  useCurrentOrganization,
  useCurrentProduct,
  useCurrentReviewId,
  useCurrentService,
} from '../../store/hooks/createReviewData';
import { useReviewItem } from '../../store/hooks/reviews';
import { useFingerprint, useIsUserLogged, useUser } from '../../store/hooks/users';
import { setReviewId } from '../../store/reducers/createReviewData';
import { doDeleteReview } from '../../store/reducers/reviews';
import pageStyles from '../../styles/page.module.scss';
import { TCreateReviewPageQuery } from '../../types/router';
import { pushUserId } from '../../utils/analytics';
import { DATE_FORMAT_FOR_CREATING_REVIEW } from '../../utils/dateTime';
import { createLinkProps } from '../../utils/routing';
import { Representative } from '../Representative';
import { WhyTrustUs } from '../SideBlocks';
import { AppWithQr } from '../SideBlocks/AppWithQr/AppWithQr';
import { CreateSkeleton } from '../Skeletons/create';
import { ReviewSkeleton } from '../Skeletons/review';
import { AUTH_FIELD_NAME } from '../UI/AuthInlineForm/constants';
import { TUserChangeHandler } from '../UI/FormControl/types';

import { MainForm } from './components/MainForm';
import { NavigationHeader } from './components/NavigationHeader/NavigationHeader';
import { TDefaultValuesForAuthorization } from './components/Steps/Authorization/interfaces';
import { PublishedStep } from './components/Steps/Published/PublishedStep';
import { REVIEW_FIELDS } from './constants/common';
import styles from './create-review.module.scss';
import { EVENT_ACTIONS, REVIEW_FIELD_TO_ACTION_LABEL_MAP } from './helpers/analytics';
import { handleSubmitError } from './helpers/handleSubmitError';
import { prepareSubmitData } from './helpers/prepareSubmitData';
import { useGetInitData } from './hooks/useGetInitData';
import { createSchema } from './rules';
import { TFormInputs } from './types';

type TProps = {
  isEdit: boolean;
};

export const CreateReview: React.FC<TProps> = ({ isEdit }) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const dispatch = useDispatch();
  const router = useRouter() as TRouter<TCreateReviewPageQuery>;
  const { query } = router;
  const user = useUser();
  const fp = useFingerprint();
  const isUserLogged = useIsUserLogged();

  const { item: review } = useReviewItem();
  const reviewId = useCurrentReviewId();

  /** Условие отображения формы с личными данными в режиме редактировании
   * Если пользователь при создании поставил флаг что он НЕ клиент или оставил данные пустые, мы НЕ даем ему их редактировать,
   * флаг, что пользователь клиент, не сохраняется на бэке, мы определяем это по заполненности личных данных по полю reviewUserData.isEmpty
   * Но если данные остались пустыми, а модератор запросил документы через админку, мы их отображаем в режиме редактирования*/

  const isRequiredUserData =
    isEdit && review?.reviewUserData && !review.reviewUserData.isApproved && review?.requireUserData;

  const isReadOnly = isEdit && review && !review?.canEditReview;
  const isExtraInfoReadOnly = review && !review?.canEditReviewHiddenFields;
  const isRatingReadOnly = isReadOnly || (isEdit && checkIfModerationComplete(review));
  const isDeleteButtonVisible = isEdit && review.canDelete;

  const [isFormReadyToShow, setIsFormReadyToShow] = useBoolean(false);
  const [isSendingReview, setIsSendingReview] = useBoolean(false);
  const [publishedReviewId, setPublishedReviewId] = useState<number>(null);
  const [isDeleting, setIsDeleting] = useBoolean(false);

  const sendEventStatusRef = useRef<Record<string, boolean>>({});

  const initData = useGetInitData({
    isEdit,
  });

  const [formData, setFormData] = useState<TFormInputs>(initData);
  const [organizationResponse, setOrganizationResponse] = useState<ICompanyResponse>(null);
  const [organizationRepresentative, setOrganizationRepresentative] = useState<IRepresentative>(null);

  const organization = useCurrentOrganization();
  const product = useCurrentProduct();
  const service = useCurrentService();

  const orgType = formData?.[REVIEW_FIELDS.ORGANIZATION_TYPE];

  const organizationHelper = useMemo(() => getOrganizationHelpers(orgType), [orgType]);

  const previousUser = usePrevious(user);

  const listUrl = createLinkProps('/list', LIST, {
    type: organizationHelper.listTypeUrl,
  });

  const commonEventLabel = useMemo(
    () => [organizationHelper?.namesType?.bigFirstCharMulti ?? '', organization?.name ?? '', service?.label ?? ''],
    [organization, organizationHelper, service],
  );

  const fetchOrganizationResponse = async (id) => {
    const today = new Date();
    const res = await loadOrganizationResponse({
      organizationId: id,
      From: format(subMonths(today, 1), DATE_FORMAT_FOR_CREATING_REVIEW),
      To: format(today, DATE_FORMAT_FOR_CREATING_REVIEW),
    });

    setOrganizationResponse(res);
  };

  const fetchRepresentative = async (id) => {
    const res = await loadRepresentative(id);
    setOrganizationRepresentative(res);
  };

  useEffect(() => {
    if (isSendingReview || !isFormReadyToShow) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isFormReadyToShow, isSendingReview]);

  useEffect(() => {
    dispatch(setReviewId(review?.id));
  }, [dispatch, review]);

  useEffect(() => {
    if (isEqual(previousUser, user)) return;
    pushUserId(user);
  }, [previousUser, user]);

  useEffect(() => {
    if (!organization) {
      setOrganizationRepresentative(null);
      setOrganizationResponse(null);
    } else {
      const id = organization.id;
      fetchOrganizationResponse(id);
      fetchRepresentative(id);
    }
  }, [organization]);

  const handleSubmitEvent = useCallback(
    ({ isEdit }: { data: TFormInputs; isEdit: boolean }) => {
      sendReviewEvent({
        label: commonEventLabel,
        action: isEdit ? EVENT_ACTIONS.UPDATE : EVENT_ACTIONS.SUBMIT,
      });
    },
    [commonEventLabel],
  );

  const handleSubmit = useCallback(
    async (data: TFormInputs) => {
      try {
        setIsSendingReview.on();

        const reviewData = prepareSubmitData({
          data,
          initReview: review,
          query,
          fingerPrint: fp,
          isEdit,
          product,
        });

        if (isEdit) {
          await updateReview({ data: reviewData, initData });
          setPublishedReviewId(reviewData.id);
        } else {
          const id = await createReview(reviewData);
          setPublishedReviewId(id);
        }
        handleSubmitEvent({ data, isEdit });
      } catch (error) {
        handleSubmitError(error);
        throw error;
      } finally {
        setIsSendingReview.off();
      }
    },
    [fp, handleSubmitEvent, initData, isEdit, product, query, review, setIsSendingReview],
  );

  const handleAllChanges = useCallback((newFormValues: TFormInputs, _changedKeys: Array<keyof TFormInputs>) => {
    setFormData(newFormValues);
  }, []);

  const handleDelete = useCallback(async () => {
    setIsDeleting.on();
    try {
      await dispatch(doDeleteReview({ id: reviewId }));
    } catch (e) {
      throw e;
    } finally {
      window.location.href = listUrl.as;
      setIsDeleting.off();
    }
  }, [dispatch, listUrl.as, reviewId, setIsDeleting]);

  const schema = createSchema({
    isUserLogged,
    isRequiredUserData,
    isReadOnly,
  });

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: initData,
    shouldFocusError: true,
  });

  const handleSuccessAuth = useCallback(() => {
    setIsSendingReview.on();
    sendAuthEvent();
    window.location.reload();
  }, [setIsSendingReview]);

  const handleFormReady = useCallback(() => {
    setIsFormReadyToShow.on();
  }, [setIsFormReadyToShow]);

  const defaultValuesForAuthorization: TDefaultValuesForAuthorization = useMemo(
    () => ({
      [AUTH_FIELD_NAME.PHONE]: formData[REVIEW_FIELDS.USER_PHONE],
      [AUTH_FIELD_NAME.NAME]: formData[REVIEW_FIELDS.USER_NAME],
    }),
    [formData],
  );

  if (isDeleting && !review) {
    // при обновлении страницы, когда ждём отправки отзыва
    return <ReviewSkeleton />;
  }

  const handleUserChange: TUserChangeHandler = ({ name, value }) => {
    const eventAction = REVIEW_FIELD_TO_ACTION_LABEL_MAP(name as REVIEW_FIELDS, isEdit);
    const isSendEvent = sendEventStatusRef.current[name]; // отправляем событие один раз на страницу

    if (isSendEvent || !eventAction) return;

    let eventLabel = name !== REVIEW_FIELDS.ORGANIZATION_TYPE ? commonEventLabel : [];
    switch (name) {
      case REVIEW_FIELDS.ORGANIZATION_TYPE:
        eventLabel[0] = value;
        break;
      case REVIEW_FIELDS.ORGANIZATION_ID:
        eventLabel[1] = value;
        break;
      case REVIEW_FIELDS.LOCATION_GAR_ID:
      case REVIEW_FIELDS.PRODUCT_ID:
      case REVIEW_FIELDS.SERVICE:
        eventLabel[2] = value;
        break;
      default:
        break;
    }

    sendReviewEvent({
      action: eventAction,
      label: eventLabel,
    });
    sendEventStatusRef.current[name] = true;
  };

  const isFormHidden = isSendingReview || !!publishedReviewId || !isFormReadyToShow;
  const isSkeletonVisible = isSendingReview || (!isFormReadyToShow && !publishedReviewId);

  const additionalBlocks = (
    <>
      {!publishedReviewId && <WhyTrustUs />}
      {Boolean(organizationRepresentative?.id) && organizationResponse?.avgResponseTime && (
        <Representative
          representative={organizationRepresentative}
          response={organizationResponse}
          logo={organization?.squareLogo}
        />
      )}
      <AppWithQr />
    </>
  );

  return (
    <>
      {!publishedReviewId && <NavigationHeader />}

      <div className={cn(pageStyles.containerIntoLayout, isMobile ? 'h-mt-24' : 'h-mt-32')}>
        <div className={pageStyles.mainColumn}>
          {isSkeletonVisible && <CreateSkeleton />}

          {!isSendingReview && publishedReviewId && (
            <div className={pageStyles.mainColumnInner}>
              <PublishedStep
                organizationAlias={organization.alias}
                organizationType={formData[REVIEW_FIELDS.ORGANIZATION_TYPE]}
                reviewId={publishedReviewId}
              />
            </div>
          )}

          <FormProvider {...formMethods}>
            <MainForm
              onChange={handleAllChanges}
              onSubmit={handleSubmit}
              handleDelete={handleDelete}
              isRequiredUserData={isRequiredUserData}
              isReadOnly={isReadOnly}
              isExtraInfoReadOnly={isExtraInfoReadOnly}
              isRatingReadOnly={isRatingReadOnly}
              isDeleteButtonVisible={isDeleteButtonVisible}
              isEdit={isEdit}
              isHidden={isFormHidden}
              handleUserChange={handleUserChange}
              commonEventLabel={commonEventLabel}
              schema={schema}
              handleSuccessAuth={handleSuccessAuth}
              handleFormReady={handleFormReady}
              isFormReadyToShow={isFormReadyToShow}
              defaultValuesForAuthorization={defaultValuesForAuthorization}
            />
          </FormProvider>
          {isMobile && !isFormHidden && (
            <Space
              className={cn(pageStyles.mainColumnInnerOnlyMobile, styles.additionalBlock, 'h-mt-16')}
              size={16}
              direction={isTablet ? 'horizontal' : 'vertical'}
              align={isTablet ? 'flex-start' : 'center'}
              justify={isTablet ? 'flex-start' : 'center'}
            >
              {additionalBlocks}
            </Space>
          )}
        </div>
        <Space className={pageStyles.rightColumn} direction={'vertical'} size={24}>
          {additionalBlocks}
        </Space>
      </div>
    </>
  );
};
